import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import SettingsIcon from "@mui/icons-material/Settings";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ticketLifecycleApi, {
  ExecutiveOverviewResponse,
  SnoozedTicket,
  StaleTicketConfig,
  SyncStatus,
  TechnicianOverviewResponse,
} from "../../apis/ticketLifecycleApi";
import ExecutiveOverview from "./ExecutiveOverview";
import TechnicianOverview from "./TechnicianOverview";
import SnoozedTickets from "./SnoozedTickets";
import StaleTicketConfigForm from "./StaleTicketConfigForm";

/** "2 min ago" / "3 hours ago" / "never" — relative to now (browser-local). */
function formatRelative(iso?: string | null): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "never";
  const sec = Math.round((Date.now() - then) / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr !== 1 ? "s" : ""} ago`;
  const day = Math.round(hr / 24);
  return `${day} day${day !== 1 ? "s" : ""} ago`;
}

/** Absolute local date+time, or "never". */
function formatAbsolute(iso?: string | null): string {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "never";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

const TicketLifecyclePanel: React.FC = () => {
  // undefined = not yet fetched, null = no config row, StaleTicketConfig = loaded
  const [config, setConfig] = useState<StaleTicketConfig | null | undefined>(undefined);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  const [executiveData, setExecutiveData] = useState<ExecutiveOverviewResponse | null>(null);
  const [technicianData, setTechnicianData] = useState<TechnicianOverviewResponse | null>(null);
  const [snoozedData, setSnoozedData] = useState<SnoozedTicket[]>([]);
  const [haloBaseUrl, setHaloBaseUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncToast, setSyncToast] = useState<{ message: string; severity: "success" | "info" | "error" } | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Sync status is best-effort: a failure here (e.g. migration not yet applied)
  // must never block the dashboard, so swallow errors.
  const refreshSyncStatus = useCallback(async () => {
    try {
      setSyncStatus(await ticketLifecycleApi.getSyncStatus());
    } catch {
      /* ignore */
    }
  }, []);

  // `silent` updates the data in place without toggling the global loading
  // spinner — used after snooze/unsnooze so the sections don't unmount/flash
  // (preserving collapse state and scroll). The initial load is non-silent.
  const fetchData = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [exec, tech, snoozed] = await Promise.all([
        ticketLifecycleApi.getExecutiveOverview(),
        ticketLifecycleApi.getTechnicianOverview(),
        ticketLifecycleApi.getSnoozed(),
      ]);
      setExecutiveData(exec);
      setTechnicianData(tech);
      setSnoozedData(snoozed);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load ticket lifecycle data";
      setError(message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Halo base URL (for deep-linking to PSA tickets) — fetched once.
  useEffect(() => {
    ticketLifecycleApi.getHaloBaseUrl().then(setHaloBaseUrl).catch(() => setHaloBaseUrl(""));
  }, []);

  // Fetch config on mount
  useEffect(() => {
    ticketLifecycleApi.getConfig().then((cfg) => {
      setConfig(cfg);
      if (cfg !== null) {
        // Config exists — also fetch stats + sync status
        fetchData();
        refreshSyncStatus();
      }
    }).catch(() => {
      // On error, treat as no config
      setConfig(null);
    });
  }, [fetchData, refreshSyncStatus]);

  const handleSnooze = useCallback(
    async (ticketId: string | number) => {
      await ticketLifecycleApi.snooze(ticketId);
      // Silent re-fetch so the ticket moves out of the technician overview and
      // into the snoozed section without flashing the whole panel.
      await fetchData({ silent: true });
    },
    [fetchData]
  );

  const handleUnsnooze = useCallback(
    async (ticketId: string | number) => {
      await ticketLifecycleApi.unsnooze(ticketId);
      await fetchData({ silent: true });
    },
    [fetchData]
  );

  const handleSync = async () => {
    setSyncing(true);
    setSyncError(null);
    setBannerDismissed(false); // a new attempt re-surfaces the banner if it still errors
    try {
      const result = await ticketLifecycleApi.sync();
      if (result?.disabled) {
        setSyncToast({
          message:
            "Ticket Lifecycle sync is not enabled for this customer (no Halo platform or config disabled).",
          severity: "info",
        });
      } else {
        await fetchData();
        setSyncToast({
          message: `Sync complete — ${result?.tickets_upserted ?? 0} tickets, ${
            result?.appointments_upserted ?? 0
          } calls, ${result?.insights_generated ?? 0} insights updated.`,
          severity: "success",
        });
      }
    } catch (err: unknown) {
      // Errors are shown loudly in the persistent banner (via syncError + the
      // persisted sync status), not a disappearing toast.
      const message = err instanceof Error ? err.message : "Sync failed";
      setSyncError(message);
    } finally {
      setSyncing(false);
      refreshSyncStatus();
    }
  };

  // Derived: either pass is actively running.
  const isSyncing =
    syncStatus?.structural.state === 'syncing' ||
    syncStatus?.classifier.state === 'syncing' ||
    false;

  // Derived: the later of the two last_run_at timestamps (whichever pass ran most recently).
  const lastUpdated: string | null = (() => {
    if (!syncStatus) return null;
    const a = syncStatus.structural.last_run_at;
    const b = syncStatus.classifier.last_run_at;
    if (!a && !b) return null;
    if (!a) return b;
    if (!b) return a;
    return new Date(a) >= new Date(b) ? a : b;
  })();

  // Ref must be declared before early returns (rules of hooks).
  const prevIsSyncingRef = useRef(false);

  // Poll sync-status every 4 s while a pass is running.
  // On syncing → idle transition, silently refresh the dashboard data.
  useEffect(() => {
    const prev = prevIsSyncingRef.current;
    prevIsSyncingRef.current = isSyncing;

    if (prev && !isSyncing) {
      fetchData({ silent: true });
    }

    if (!isSyncing) return;

    const id = setInterval(refreshSyncStatus, 4000);
    return () => clearInterval(id);
  }, [isSyncing, refreshSyncStatus, fetchData]);

  // Still loading config
  if (config === undefined) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // No config row yet, OR feature disabled — show the configuration form as a
  // centered, width-limited card instead of the overview. Pre-populate with the
  // existing config when one exists (disabled) so the user can flip Enabled on.
  if (config === null || !config.enabled) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "70vh" }}>
        <Paper elevation={4} sx={{ width: "65vw", maxWidth: 1120, borderRadius: 3 }}>
          <StaleTicketConfigForm
            initialConfig={config}
            onSaved={(cfg) => {
              setConfig(cfg);
              fetchData();
              // Enabling triggers a background sync server-side — start watching it.
              refreshSyncStatus();
            }}
          />
        </Paper>
      </Box>
    );
  }

  // Config exists — show stats + Configure button.
  // Build the "loud" warnings from the persisted sync status + (only if not already
  // persisted) the live sync error, so a single failure doesn't show twice.
  const warnings: string[] = [];
  if (syncStatus) {
    const s = syncStatus.structural;
    const c = syncStatus.classifier;
    // Whole-pass hard failures stay global. Per-ticket appointment/summary
    // failures are now localized (calls-area notice / red-tinted summary), so
    // they're intentionally NOT aggregated into this banner anymore.
    if (s.error) warnings.push(`Halo sync failed: ${s.error}`);
    if (s.state === 'interrupted')
      warnings.push("Halo sync didn't finish — it may have been interrupted.");
    if (c.error) warnings.push(`Summary generation failed: ${c.error}`);
    if (c.state === 'interrupted')
      warnings.push("Summary generation didn't finish — it may have been interrupted.");
    // NOTE: the old "lookback gap" warning (tracked activity older than the 7-day
    // poller window) was removed. It fired whenever an MSP simply had no recent
    // Halo activity — a normal state, not a problem — and Sync now could never
    // clear it (there was no newer data to pull, and Sync now doesn't run the
    // past_tickets backfill that actually covers historical coverage). Historical
    // open tickets are handled by the backfill layer, so a stale cursor is expected.
  }
  // The live sync error (from the last Sync now) is only added when the DB didn't
  // already persist a pass error — otherwise the same failure appears twice.
  const hasPersistedError = !!(syncStatus && (syncStatus.structural.error || syncStatus.classifier.error));
  if (syncError && !hasPersistedError) warnings.push(syncError);
  const uniqueWarnings = Array.from(new Set(warnings));
  const showBanner = uniqueWarnings.length > 0 && !bannerDismissed;

  return (
    <Box>
      {showBanner && (
        <Alert
          severity="warning"
          onClose={() => setBannerDismissed(true)}
          action={
            <Button color="inherit" size="small" onClick={handleSync} disabled={syncing || isSyncing}>
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          <AlertTitle>Ticket Lifecycle sync had problems</AlertTitle>
          {uniqueWarnings.map((w, i) => (
            <Box component="div" key={i}>
              {w}
            </Box>
          ))}
        </Alert>
      )}

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {syncStatus && (
            <>
              {isSyncing ? (
                <>
                  <CircularProgress size={12} sx={{ color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary">
                    Syncing…
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Last updated at: {formatRelative(lastUpdated)}
                </Typography>
              )}
            </>
          )}
          <Tooltip
            arrow
            title={
              <Box sx={{ maxWidth: 260 }}>
                <Typography variant="caption" sx={{ display: "block", mb: 0.75 }}>
                  Last updated at is the latest data refresh — use Sync now if it looks out of date.
                </Typography>
                <Typography variant="caption" sx={{ display: "block" }}>
                  Halo sync: {formatAbsolute(syncStatus?.structural.last_run_at)}
                </Typography>
                <Typography variant="caption" sx={{ display: "block" }}>
                  Summaries: {formatAbsolute(syncStatus?.classifier.last_run_at)}
                </Typography>
              </Box>
            }
          >
            <InfoOutlinedIcon sx={{ fontSize: 15, color: "text.disabled", cursor: "help" }} />
          </Tooltip>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => setConfigDialogOpen(true)}
          >
            Configure
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing || isSyncing}
          >
            Sync now
          </Button>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && (
        <>
          {executiveData && <ExecutiveOverview data={executiveData} />}
          {technicianData && (
            <>
              <Divider sx={{ my: 3 }} />
              <TechnicianOverview
                data={technicianData}
                onSnooze={handleSnooze}
                haloBaseUrl={haloBaseUrl}
                appointmentsPermissionDenied={!!syncStatus?.appointments_permission_denied}
              />
            </>
          )}
          <Divider sx={{ my: 3 }} />
          <SnoozedTickets
            data={snoozedData}
            onUnsnooze={handleUnsnooze}
            haloBaseUrl={haloBaseUrl}
          />
        </>
      )}

      <Snackbar
        open={syncToast !== null}
        autoHideDuration={6000}
        onClose={() => setSyncToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {syncToast ? (
          <Alert severity={syncToast.severity} onClose={() => setSyncToast(null)} sx={{ width: "100%" }}>
            {syncToast.message}
          </Alert>
        ) : undefined}
      </Snackbar>

      <Dialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: { width: "65vw", maxWidth: 1120 } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <StaleTicketConfigForm
            initialConfig={config}
            onSaved={(cfg) => {
              setConfig(cfg);
              setConfigDialogOpen(false);
              fetchData();
              refreshSyncStatus();
            }}
            onCancel={() => setConfigDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TicketLifecyclePanel;
